import React from 'react'
import { Box, Card, CardMedia, Dialog, DialogContent, Paper, Stack, Table, TableBody, TableContainer, TableHead, TableRow, Typography } from '@mui/material'
import { grey } from '@mui/material/colors'
import { useGlobalState } from 'state'
import { HashMap, ObservatoryType, SensorType, SuijinValue } from 'common/types'
import { SuijinStatus } from 'common/consts'
import { StyledTableCell } from 'components/ui/StyledTableCell'
import { StyledTableRow } from 'components/ui/StyledTableRow'
import { SuijinChart } from './SuijinChart'
import dayjs from 'dayjs'

const AlertLevelRow = ({color, level}: {color: string, level?: number}) => {
  if (level === undefined) return <></>
  return (
    <Stack direction="row" alignItems={'center'} sx={{pl: 3}}>
      <Box width={50}>
        <Box width={40} height={1} borderBottom={`3px dashed ${color}`}></Box>
      </Box>
      <Typography variant='caption' width={60} textAlign={'right'}>{level} m</Typography>
    </Stack>
  )
}

export const SuijinDetail = () => {
  const state = useGlobalState()
  const [imageDialogOpen, setImageDialogOpen] = React.useState(false)

  const observatory = React.useMemo(() => {
    return state.observatory.selected
  }, [state.observatory.selected])

  const imageUrl = React.useMemo(() => {
    if (observatory === null) return ''
    const imageId = observatory.id.replace('22100_', '')
    return `https://d1zetlo8apprf7.cloudfront.net/static/suijin_sensor/${imageId}.jpg`
  }, [observatory])

  const sensors: SensorType[] = React.useMemo(() => {
    if (observatory === null || !observatory.sensors) return []
    return observatory.sensors
  }, [observatory])

  const sensorLevels: {[key: string]: number | null} = React.useMemo(() => {
    return sensors.reduce((acc: {[key: string]: number | null}, current: SensorType) => {
      acc[current.id] = current.level
      return acc
    }, {})
  }, [sensors])

  const waterBoxLevel: number | null = React.useMemo(() => {
    if (observatory === null || state.observatory.observatories.length === 0) return null
    const target = state.observatory.observatories.find(o => o.id === observatory.id && o.type === ObservatoryType.Suijin)
    if (target && target.level !== undefined && target.level !== null) {
      return target.level
    } else {
      return null
    }
  }, [state.observatory.observatories, observatory])

  const timeSeries: SuijinValue[] = React.useMemo(() => {
    if (observatory === null) return []
    if (state.observatory.timeSeries.length === 0) return []

    const minDate = dayjs.utc(state.app.baseDate).subtract(4, 'hours')
    const targets = (state.observatory.timeSeries).filter(ts =>
      ts.level !== null &&
      ts.level !== undefined &&
      ts.status !== undefined &&
      Object.values(ts.status).some(val => ['05', '06', '09'].includes(val)) &&
      ts.date > minDate.format('YYYY/MM/DD HH:mm')
    )
    return targets.map(ts => {
      const rowSensorLevels: HashMap = { ...sensorLevels }
      Object.keys(rowSensorLevels).forEach(sensorId => {
        if (ts.status !== undefined && ['05', '06'].includes(ts.status[sensorId]) === false) {
          rowSensorLevels[sensorId] = '-'
        } else if (ts.level === null || ts.level === undefined) {
          rowSensorLevels[sensorId] = '-'
        } else {
          rowSensorLevels[sensorId] = ts.level.toFixed(2)
        }
      })
      return {
        date: ts.date,
        type: ts.type,
        level: ts.level,
        status: ts.status,
        sensorLevels: rowSensorLevels
      }
    }) as SuijinValue[]
  }, [state.observatory.timeSeries])

  const timeSeriesForTable: SuijinValue[] = React.useMemo(() => {
    return timeSeries.map(ts => {
      const rowSensorLevels: HashMap = { ...sensorLevels }
      Object.keys(rowSensorLevels).forEach(sensorId => {
        if (ts.status === undefined) {
          rowSensorLevels[sensorId] = '-'
        } else if (ts.status[sensorId] === '05') {
          rowSensorLevels[sensorId] = '検知'
        } else if (ts.status[sensorId] === '06') {
          rowSensorLevels[sensorId] = '継続'
        } else if (ts.status[sensorId] === '09') {
          rowSensorLevels[sensorId] = '解除'
        } else {
          rowSensorLevels[sensorId] = '-'
        }
      })
      return {
        date: ts.date,
        type: ts.type,
        level: ts.level,
        status: ts.status,
        sensorLevels: rowSensorLevels
      }
    })
  }, [timeSeries, sensors])

  const getLocalDateFormat = (date: string) => {
    return dayjs.utc(date).local().format('MM/DD HH:mm')
  }

  return ( observatory &&
    <Box>

      <Stack direction="column">
        <Typography variant='caption'>（m）</Typography>
        <SuijinChart observatory={observatory} height={200} timeSeries={timeSeries} waterBoxLevel={waterBoxLevel} />
      </Stack>

      <Stack direction="row" alignItems={'center'} my={1}>
        <Stack direction="column" color={grey[900]} flexGrow={1}>
          <Typography variant='caption'>センサ検知高さ</Typography>
          <AlertLevelRow color={SuijinStatus.FLOOD4.color} level={sensors.length > 0 ? sensors[0].level : undefined} />
          <AlertLevelRow color={SuijinStatus.FLOOD3.color} level={sensors.length > 1 ? sensors[1].level : undefined} />
          <AlertLevelRow color={SuijinStatus.FLOOD2.color} level={sensors.length > 2 ? sensors[2].level : undefined} />
          <AlertLevelRow color={SuijinStatus.FLOOD1.color} level={sensors.length > 3 ? sensors[3].level : undefined} />
        </Stack>
        { imageUrl &&
          <Card>
            <CardMedia
              component="img"
              height={70}
              image={imageUrl}
              sx={{cursor: 'pointer'}}
              onClick={() => setImageDialogOpen(true)}
            />
          </Card>
        }
      </Stack>

      <Dialog open={imageDialogOpen} onClose={() => setImageDialogOpen(false)} maxWidth='md'>
        <DialogContent sx={{padding: 1}} onClick={() => setImageDialogOpen(false)}>
          <img src={imageUrl} alt="Suijin Sensor" style={{width: '100%'}} />
        </DialogContent>
      </Dialog>

      <Stack direction="row" alignItems={'center'} my={2}>
        <Typography variant='caption'>
          { observatory.status === 'waiting' && 'この観測所は待機中です。'}
          { observatory.status === 'maintenance' && 'この観測所はメンテナンス中です。'}
        </Typography>
      </Stack>

      { timeSeriesForTable.length > 0 && (
        <TableContainer component={Paper}>
          <Table size="small" aria-label="customized table">
            <TableHead>
              <TableRow>
                <StyledTableCell width={190} sx={{p: 1}}>日時</StyledTableCell>
                { sensors.map((sensor, index) => <StyledTableCell key={index} width={90} sx={{textAlign: 'right', p: 1}}>{`${sensor.level}m`}</StyledTableCell>) }
              </TableRow>
            </TableHead>
            <TableBody>
              {timeSeriesForTable.map((ts, i) => (
                <StyledTableRow key={i}>
                  <StyledTableCell component="th" scope="row" color={grey[900]} sx={{p: 1}}>
                    <Typography variant='body2'>{getLocalDateFormat(ts.date)}</Typography>
                  </StyledTableCell>
                  {
                    Object.values(ts.sensorLevels).map((level, index) => {
                      return (
                        <StyledTableCell key={index} sx={{p: 1}}>
                          <Typography variant='body2' textAlign={'right'} color={grey[900]}>
                            { level }
                          </Typography>
                        </StyledTableCell>
                      )
                    })
                  }
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  )
}
